import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import { getScopeForm } from '@shared/api/scope-form';
import type { InvoicePaymentMode, Uuid } from '@shared/types/api';
import { useCreateInvoice } from '@/hooks/use-admin';

const MODE_TONE: Record<InvoicePaymentMode, string> = {
  ONLINE: 'bg-blue-50 text-blue-800',
  OFFLINE: 'bg-gray-100 text-gray-700',
};

export function CreateInvoiceDialog({
  transactionId,
  onClose,
  isStartsFrom = false,
  defaultPlaceOfSupply = 'Tamil Nadu',
  defaultAssociateFeeRupees = '',
  defaultPlatformFeeRupees = '',
  defaultApplicationFeeRupees = '',
  defaultServiceChargeRupees = '',
  defaultGovernmentFeeRupees = '',
  additionalCharges = [],
}: {
  transactionId: Uuid;
  onClose: () => void;
  isStartsFrom?: boolean;
  defaultPlaceOfSupply?: string;
  defaultAssociateFeeRupees?: string;
  defaultPlatformFeeRupees?: string;
  defaultApplicationFeeRupees?: string;
  defaultServiceChargeRupees?: string;
  defaultGovernmentFeeRupees?: string;
  additionalCharges?: { id?: string; name?: string; amountRupees?: string; gstPercentage?: string; hsnSacCode?: string }[];
}) {
  const [paymentMode, setPaymentMode] = useState<InvoicePaymentMode>('OFFLINE');
  const initialAssocFee = isStartsFrom ? '' : (defaultAssociateFeeRupees || defaultApplicationFeeRupees);
  const initialPlatformFee = isStartsFrom ? '' : (defaultPlatformFeeRupees || defaultServiceChargeRupees);
  const initialGovtFee = isStartsFrom ? '' : defaultGovernmentFeeRupees;

  const [applicationFeeRupees, setApplicationFeeRupees] = useState(initialAssocFee);
  const [serviceChargeRupees, setServiceChargeRupees] = useState(initialPlatformFee);
  const [governmentFeeRupees, setGovernmentFeeRupees] = useState(initialGovtFee);
  const [placeOfSupplyState, setPlaceOfSupplyState] = useState(defaultPlaceOfSupply);
  const [clientGstin, setClientGstin] = useState('');

  const { data: scopeForm } = useQuery({
    queryKey: ['scope-form', transactionId],
    queryFn: () => getScopeForm(transactionId),
    enabled: !!transactionId,
  });

  useEffect(() => {
    if (scopeForm) {
      if (scopeForm.professionalFeeRupees) {
        setApplicationFeeRupees(scopeForm.professionalFeeRupees);
      }
      if (scopeForm.platformFeeRupees) {
        setServiceChargeRupees(scopeForm.platformFeeRupees);
      }
      if (scopeForm.governmentFeeRupees) {
        setGovernmentFeeRupees(scopeForm.governmentFeeRupees);
      }
    }
  }, [scopeForm]);

  const initialAddl = (additionalCharges && additionalCharges.length > 0)
    ? additionalCharges.map((ch, idx) => {
        const rawName = (ch.name || '').trim();
        const cleanName = (rawName.toLowerCase() === 'service' || rawName.toLowerCase() === 'additional charge') ? '' : rawName;
        return {
          id: ch.id || String(idx),
          name: cleanName,
          amountRupees: isStartsFrom ? '' : (ch.amountRupees || ''),
          gstPercentage: ch.gstPercentage || '18.00',
          hsnSacCode: ch.hsnSacCode || '998213',
        };
      })
    : [{ id: 'default', name: '', amountRupees: '', gstPercentage: '18.00', hsnSacCode: '998213' }];

  const [addlChargesState, setAddlChargesState] = useState(initialAddl);
  const create = useCreateInvoice();

  function cleanRupees(raw: string): string {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    const head = parts[0] ?? '';
    const tail = parts.length > 1 ? '.' + parts.slice(1).join('').slice(0, 2) : '';
    return head + tail;
  }

  function rupeesToPaise(r: string): string | undefined {
    if (!r.trim()) return undefined;
    const n = Math.round(Number(r) * 100);
    return Number.isFinite(n) && n >= 0 ? String(n) : undefined;
  }

  function submit() {
    const formattedAddl: {
      description: string;
      amountPaise: string;
      gstPercentage?: string;
      hsnSacCode?: string;
    }[] = [];

    for (const ch of addlChargesState) {
      const amt = rupeesToPaise(ch.amountRupees || '0');
      if (amt && Number(amt) > 0) {
        const rawName = (ch.name || '').trim();
        const description = !rawName || rawName.toLowerCase() === 'service' || rawName.toLowerCase() === 'additional charge'
          ? 'Additional Charge'
          : rawName.toLowerCase().startsWith('additional charge')
          ? rawName
          : `Additional Charge (${rawName})`;
        formattedAddl.push({
          description,
          amountPaise: amt,
          ...(ch.gstPercentage ? { gstPercentage: ch.gstPercentage } : {}),
          ...(ch.hsnSacCode ? { hsnSacCode: ch.hsnSacCode } : {}),
        });
      }
    }

    create.mutate(
      {
        transactionId,
        paymentMode,
        applicationFeePaise: rupeesToPaise(applicationFeeRupees),
        serviceChargePaise: rupeesToPaise(serviceChargeRupees),
        governmentFeePaise: rupeesToPaise(governmentFeeRupees),
        additionalCharges: formattedAddl.length > 0 ? formattedAddl : undefined,
        placeOfSupplyState: placeOfSupplyState.trim(),
        clientGstin: clientGstin.trim() || undefined,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Create invoice"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create invoice'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Creating invoice for transaction{' '}
          <span className="font-mono text-gray-900">{transactionId}</span>. Transitions the
          transaction to <strong>INVOICE_CREATED</strong>.
        </div>

        <div>
          <div className="mb-1 text-xs font-medium text-gray-700">Payment mode</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <ModeOption
              mode="ONLINE"
              selected={paymentMode === 'ONLINE'}
              onClick={() => setPaymentMode('ONLINE')}
              title="Online (Razorpay)"
              body="Client pays via UPI/card in the app. Webhook closes the loop automatically."
              disabled={true}
            />
            <ModeOption
              mode="OFFLINE"
              selected={paymentMode === 'OFFLINE'}
              onClick={() => setPaymentMode('OFFLINE')}
              title="Offline"
              body="Admin coordinates payment out-of-band, then records it via Mark paid offline."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <L label="Associate fee (₹)">
            <Input
              inputMode="decimal"
              value={applicationFeeRupees}
              onChange={(e) => setApplicationFeeRupees(cleanRupees(e.target.value))}
              placeholder="0.00"
            />
          </L>
          <L label="Platform fee (₹)">
            <Input
              inputMode="decimal"
              value={serviceChargeRupees}
              onChange={(e) => setServiceChargeRupees(cleanRupees(e.target.value))}
              placeholder="0.00"
            />
          </L>
          <L label="Government fee (₹)">
            <Input
              inputMode="decimal"
              value={governmentFeeRupees}
              onChange={(e) => setGovernmentFeeRupees(cleanRupees(e.target.value))}
              placeholder="0.00"
            />
          </L>
        </div>

        <div className="rounded-md border border-gray-200 bg-gray-50/80 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Additional Charges
            </span>
            <button
              type="button"
              onClick={() =>
                setAddlChargesState((prev) => [
                  ...prev,
                  { id: crypto.randomUUID(), name: '', amountRupees: '', gstPercentage: '18.00', hsnSacCode: '998213' },
                ])
              }
              className="text-xs font-semibold text-brand-700 hover:underline"
            >
              + Add charge
            </button>
          </div>

          <div className="space-y-2">
            {addlChargesState.map((ch, idx) => (
              <div key={ch.id || idx} className="flex gap-2 items-center">
                <div className="flex-1">
                  <Input
                    value={ch.name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAddlChargesState((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, name: val } : item))
                      );
                    }}
                    placeholder="Charge description (optional)"
                    className="h-8 text-xs bg-white"
                  />
                </div>
                <div className="w-32 shrink-0">
                  <Input
                    inputMode="decimal"
                    value={ch.amountRupees}
                    onChange={(e) => {
                      const val = cleanRupees(e.target.value);
                      setAddlChargesState((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, amountRupees: val } : item))
                      );
                    }}
                    placeholder="0.00"
                    className="h-8 text-xs bg-white"
                  />
                </div>
                {addlChargesState.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setAddlChargesState((prev) => prev.filter((_, i) => i !== idx))
                    }
                    className="text-red-600 font-bold px-1 text-xs hover:underline"
                    title="Remove charge"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <L label="Place of supply (state)">
          <Input
            value={placeOfSupplyState}
            onChange={(e) => setPlaceOfSupplyState(e.target.value)}
          />
        </L>
        <L label="Client GSTIN (optional — B2B)">
          <Input
            value={clientGstin}
            onChange={(e) => setClientGstin(e.target.value.toUpperCase())}
          />
        </L>
        {create.isError ? (
          <p className="text-sm text-red-600">{apiErrorMessage(create.error)}</p>
        ) : null}
      </div>
    </Dialog>
  );
}

function ModeOption({
  mode,
  selected,
  onClick,
  title,
  body,
  disabled,
}: {
  mode: InvoicePaymentMode;
  selected: boolean;
  onClick: () => void;
  title: string;
  body: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-md border p-3 text-left text-xs',
        selected
          ? 'border-brand-600 bg-brand-50/40'
          : 'border-gray-200 bg-white hover:bg-gray-50',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-white',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{title}</span>
        <span
          className={cn(
            'rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase',
            MODE_TONE[mode],
          )}
        >
          {mode}
        </span>
      </div>
      <div className="mt-1 text-gray-600">{body}</div>
    </button>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
    </label>
  );
}
